import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ConsentSettingsUpdate } from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Checkbox } from '@london-life/ui/components/checkbox';
import { Label } from '@london-life/ui/components/label';
import { Textarea } from '@london-life/ui/components/textarea';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useRequest } from '../lib/use-request';

// super admin only: the cookie banner players see (UK PECR). Essential cookies are always on; analytics only
// load for players who accept. "Ask everyone again" is for when the cookies we use change.
export function CookiesPage() {
  const current = useRequest(useCallback(() => api.admin.consentSettings(), []));
  const [enabled, setEnabled] = useState(true);
  const [message, setMessage] = useState('');
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [askAgain, setAskAgain] = useState(false);
  const [saved, setSaved] = useState<number | null>(null);
  useEffect(() => {
    if (!current.data) return;
    setEnabled(current.data.consent.enabled);
    setMessage(current.data.consent.message);
    setAnalyticsEnabled(current.data.consent.analyticsEnabled);
  }, [current.data]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaved(null);
    const parsed = ConsentSettingsUpdate.safeParse({ enabled, message, analyticsEnabled, askAgain });
    if (!parsed.success) return current.fail(new Error(parsed.error.issues[0]?.message));
    try {
      const reply = await api.admin.saveConsentSettings(parsed.data);
      setSaved(reply.consent.version);
      setAskAgain(false);
    } catch (problem) {
      current.fail(problem);
    }
  };
  return (
    <section>
      <PageHeader
        title="Cookies"
        description="The cookie banner players see. Essential cookies (sign-in and security) are always on; analytics run only for players who accept."
      />
      {current.error && <ErrorNotice error={current.error} onRetry={() => void current.reload()} />}
      {current.data && (
        <form className="admin-panel admin-form" onSubmit={(event) => void submit(event)}>
          <label className="admin-checklist-item">
            <Checkbox checked={enabled} onCheckedChange={(state) => setEnabled(state === true)} />
            <span>Show the cookie banner</span>
          </label>
          <div className="admin-field">
            <Label htmlFor="consent-message">Banner text</Label>
            <Textarea
              id="consent-message"
              rows={4}
              maxLength={400}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
            <p className="admin-muted">20 to 400 characters. A link to the Privacy notice is added after it.</p>
          </div>
          <label className="admin-checklist-item">
            <Checkbox checked={analyticsEnabled} onCheckedChange={(state) => setAnalyticsEnabled(state === true)} />
            <span>We use analytics cookies (players get an Accept button)</span>
          </label>
          <label className="admin-checklist-item">
            <Checkbox checked={askAgain} onCheckedChange={(state) => setAskAgain(state === true)} />
            <span>Ask every player again (use when the cookies we use change)</span>
          </label>
          <p className="admin-muted">Current version: {current.data.consent.version}</p>
          <div className="admin-page-actions">
            <Button type="submit">Save</Button>
            {saved !== null && (
              <span role="status" className="admin-muted">
                Saved (version {saved}). Players see it within a minute.
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
